import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';

import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import Button from 'src/componentes/button';
import LoadingContainer from 'src/componentes/loading-container';
import Input from 'src/componentes/input';
import { Row, Col, Label } from 'reactstrap';
import Dialog from 'src/componentes/dialog';
import OrganizacaoLookup from 'src/lookups/item-organizacao';
import ColaboradorSimplificadoLookup from 'src/lookups/ColaboradorSimplificadoLookup';
import useAuth from 'src/hooks/useAuth';
import { capitalizeFirstLetter } from 'src/utils/string';
import Panel from 'src/componentes/panel-collapse';
import Alert from 'src/componentes/message-box/alert';
import ContentManager from 'src/componentes/content-manager';
import DesenharCampoCustomizado from 'src/paginas/item/campo-customizado/componentes/desenhar';
import InputNumber from 'src/componentes/input-number';
import DatePicker from 'src/componentes/date-picker';
import Tab from 'src/componentes/tabs/tab';
import Tabs from 'src/componentes/tabs/tabs';
import { getConfiguration } from 'src/services/commonRequests';
import TemplatePerdaOperacionalLookup from 'src/lookups/TemplatePerdaOperacionalLookup';
import { PERDA_OPERACIONAL_STATUS } from 'src/paginas/gestao-risco/constants/BeltConstants';
import PerguntasCampoCustomizado from 'src/paginas/gestao-risco/perda-operacional/Componentes/Perguntas/desenhar';
import ButtonAprovarDevolver from 'src/paginas/gestao-risco/perda-operacional/Componentes/ButtonToggle/ButtonToggle';
import HistoricoView from 'src/paginas/gestao-risco/perda-operacional/Componentes/Historico/HistoricoView';
import Acao from 'src/paginas/NewGantt';
import ArquivoUploadAnexos from 'src/componentes/arquivo-upload/anexos';
import StatusBadge from 'src/componentes/StatusBadge';

const PerdaOperacionalEdit = ({
  item,
  handleRefresh,
  endpoint,
  configuracao,
  disabled,
  isNew,
  ...rest
}) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const ref = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [tabsError, setTabsError] = useState([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [isNewState, setIsNew] = useState(isNew);
  const [configuracaoPerda, setConfiguracaoPerda] = useState(configuracao);

  const isDisabled =
    !form?.permissions?.allowEdit ||
    (isNewState && disabled) ||
    form?.status?.id === PERDA_OPERACIONAL_STATUS.PENDENTE_ANALISE ||
    form?.status?.id === PERDA_OPERACIONAL_STATUS.PENDENTE_REVISAO ||
    form?.status?.id === PERDA_OPERACIONAL_STATUS.CONCLUIDO;
  const usuarioPodeEnviarParaRevisao =
    form?.templatePerdaOperacional &&
    form?.status?.id === PERDA_OPERACIONAL_STATUS.PENDENTE_ANALISE &&
    form?.responsavelPelaAnalise?.id === form?.usuario?.id;
  const usuarioPodeAprovarOuDevolver =
    form?.templatePerdaOperacional &&
    form?.status?.id === PERDA_OPERACIONAL_STATUS.PENDENTE_REVISAO &&
    form?.responsaveis?.some((r) => r.id === form?.usuario?.id);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangeCamposCustomizados = (value) => {
    setForm((prev) => ({
      ...prev,
      camposCustomizadosPerdaOperacionalAnalise: value,
    }));
  };

  useEffect(() => {
    if (!configuracaoPerda || Object.keys(configuracaoPerda).length === 0)
      getConfiguration('ConfiguracaoPerdaOperacional')(setConfiguracaoPerda)();
  }, []);

  const getModel = async () => {
    const id = form?.id ? form.id : item?.id;

    try {
      setIsLoading(true);
      const url = !isNewState ? `${endpoint}/Edit` : `${endpoint}/Create`;
      const response = await http.post(url, { id });

      setForm({ ...response.data });
      setIsNew(!id || id < 0);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getModel();
  }, []);

  const isEmptyRequiredValue = (value) =>
    value == null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

  const isCampoCustomizadoSemValor = (campo) => {
    if (campo?.campo?.multiSelecao) {
      return !campo?.campo?.valores || campo.campo.valores.length === 0;
    }

    return isEmptyRequiredValue(campo?.valor);
  };

  const validateRequiredField = (form) => {
    const ignoreIdIntegracao =
      configuracaoPerda?.utilizarCodigoAutomatico && form.id <= 0;

    const requiredFields = ignoreIdIntegracao
      ? [
          'nome',
          'responsaveis',
          'area',
          'dataRegistro',
          'dataPerda',
          'valorPerda',
          'templatePerdaOperacional',
        ]
      : [
          'nome',
          'idIntegracao',
          'responsaveis',
          'area',
          'dataRegistro',
          'dataPerda',
          'valorPerda',
          'templatePerdaOperacional',
        ];

    const errors = {};
    const tabsWithError = new Set();
    let hasError = false;
    let hasCustomFieldsError = false;

    for (const field of requiredFields) {
      const value = form?.[field];

      if (isEmptyRequiredValue(value)) {
        errors[field] = true;
        hasError = true;
        tabsWithError.add(0);
      }
    }

    form?.camposCustomizadosPerdaOperacionalAnalise &&
      form?.camposCustomizadosPerdaOperacionalAnalise.forEach((campo) => {
        const campoObrigatorioSemValor =
          campo.campo.obrigatorio &&
          !campo.disabled &&
          isCampoCustomizadoSemValor(campo);

        if (campoObrigatorioSemValor) {
          hasCustomFieldsError = true;
        } else if (
          campo.usarEvidencia &&
          campo.evidenciaObrigatoria &&
          !campo.disabled &&
          (!campo.anexos || campo.anexos.length == 0)
        ) {
          hasCustomFieldsError = true;
        } else if (
          campo.usarJustificativa &&
          campo.justificativaObrigatoria &&
          !campo.disabled &&
          !campo.justificativa
        ) {
          hasCustomFieldsError = true;
        }
      });

    if (hasCustomFieldsError) {
      hasError = true;
      tabsWithError.add(1);
    }

    const newTabsError = Array.from(tabsWithError);

    setErrors(errors);
    setShowErrors(hasCustomFieldsError);
    setTabsError(newTabsError);

    if (newTabsError.length > 0) setCurrentTabIndex(newTabsError[0]);

    return { hasError, errors, tabsError: newTabsError };
  };

  const validateRequiredFieldEnvioAnalise = (form) => {
    const requiredFieldsEnvioAnalise = [
      'areaGeradora',
      'responsavelPelaAnalise',
    ];

    const errors = {};
    let hasError = false;

    for (const field of requiredFieldsEnvioAnalise) {
      const value = form?.[field];
      if (value == null || !value || value == []) {
        errors[field] = true;
        hasError = true;
      }
    }

    setErrors(errors);

    return { hasError };
  };

  const handleClose = () => {
    if (ref.current) ref.current.close();
  };

  const handleSave = async (fecharModal = false) => {
    var { hasError } = validateRequiredField(form);

    if (hasError)
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
          })}
        />
      );
    else {
      try {
        setIsLoading(true);
        await http
          .post(`${endpoint}/Save`, {
            model: {
              ...form,
              id: form?.id ? form.id : item.id,
            },
          })
          .then((response) => {
            enqueueSnackbar(
              intl.formatMessage({ id: 'label.registroSalvoComSucesso' }),
              {
                variant: 'success',
              }
            );
            if (fecharModal) {
              handleRefresh && handleRefresh();
              handleClose(true);
            } else {
              setForm(response.data?.model);
              if (isNewState) setIsNew(false);

              handleRefresh && handleRefresh();
            }
          });
      } catch (err) {
        errorHandler(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSaveAndCloseClick = async () => {
    handleSave(true);
  };

  const handleEnviarParaAnalise = async () => {
    var { hasError } = validateRequiredFieldEnvioAnalise(form);
    if (hasError)
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
          })}
        />
      );
    else {
      try {
        setIsLoading(true);
        await http
          .post(`${endpoint}/EnviarParaAnalise`, {
            model: {
              ...form,
              id: form?.id ? form.id : item.id,
            },
          })
          .then(() => {
            enqueueSnackbar(
              intl.formatMessage({ id: 'label.registroSalvoComSucesso' }),
              {
                variant: 'success',
              }
            );

            handleRefresh && handleRefresh();
            handleClose(true);
          });
      } catch (err) {
        errorHandler(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEnviarParaRevisao = async () => {
    var { hasError } = validateRequiredField(form);

    if (hasError) {
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
          })}
        />
      );
      return;
    }

    try {
      setIsLoading(true);
      await http
        .post(`${endpoint}/EnviarParaRevisao`, {
          model: {
            ...form,
            id: form?.id ? form.id : item.id,
          },
        })
        .then(() => {
          enqueueSnackbar(
            intl.formatMessage({ id: 'label.registroSalvoComSucesso' }),
            {
              variant: 'success',
            }
          );

          handleRefresh && handleRefresh();
          handleClose(true);
        });
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatus = (form) => {
    try {
      const status = form?.status;

      if (!status?.cor && !status?.corFundo) return null;

      return (
        <div style={{ marginBottom: '1rem' }}>
          <Label>{intl.formatMessage({ id: 'status' })}</Label>
          <div>
            <StatusBadge
              text={status?.descricao}
              textColor={status?.corFundo ? status?.cor : undefined}
              backgroundColor={status?.corFundo || status?.cor}
            />
          </div>
        </div>
      );
    } catch (e) {
      errorHandler(e);
    }
  };

  const editActions = (form) =>
    [
      <Button key="cancel" onClick={handleClose} color="secondary">
        {intl.formatMessage({ id: 'label.cancelar' })}
      </Button>,

      <Button
        key="save"
        disabled={
          isDisabled &&
          !usuarioPodeEnviarParaRevisao &&
          !usuarioPodeAprovarOuDevolver
        }
        type="primary"
        onClick={() => handleSave(false)}
        htmlType="submit"
        className="ml-2"
      >
        {intl.formatMessage({ id: 'label.salvar' })}
      </Button>,

      <Button
        key="saveClose"
        disabled={
          isDisabled &&
          !usuarioPodeEnviarParaRevisao &&
          !usuarioPodeAprovarOuDevolver
        }
        type="primary"
        onClick={() => handleSaveAndCloseClick(form)}
        htmlType="submit"
        className="ml-2"
      >
        {intl.formatMessage({ id: 'label.salvarEFechar' })}
      </Button>,

      !isNewState &&
        form?.templatePerdaOperacional &&
        form?.status?.id === PERDA_OPERACIONAL_STATUS.RASCUNHO && (
          <Button
            key="send"
            disabled={isDisabled}
            type="primary"
            onClick={() => handleEnviarParaAnalise(form)}
            htmlType="submit"
            className="ml-2"
          >
            {intl.formatMessage({ id: 'enviarParaAnalise' })}
          </Button>
        ),

      !isNewState && usuarioPodeEnviarParaRevisao && (
        <Button
          key="send"
          type="primary"
          onClick={() => handleEnviarParaRevisao(form)}
          htmlType="submit"
          className="ml-2"
        >
          {intl.formatMessage({ id: 'labelEnviarParaRevisao' })}
        </Button>
      ),

      !isNewState && usuarioPodeAprovarOuDevolver && (
        <ButtonAprovarDevolver
          className="ml-2"
          model={form}
          intl={intl}
          handleClose={handleClose}
          handleRefresh={handleRefresh}
          validateRequiredFields={validateRequiredField}
        />
      ),
    ].filter(Boolean);

  return (
    <Dialog
      minWidth={'60%'}
      maxWidth={'70%'}
      minHeight={'80%'}
      actions={editActions(form)}
      onRequestClose={handleClose}
      ref={ref}
      padContent
      ignoreResourceTitle={true}
      titleCapitalizeNone={true}
      textCapitalizeTitle={false}
      title={
        form?.nome
          ? resources.perdaoperacional + ' - ' + form?.nome
          : resources.perdaoperacional
      }
      {...rest}
    >
      <LoadingContainer isLoading={isLoading}>
        <Tabs
          currenttabIndex={currentTabIndex}
          onSelect={setCurrentTabIndex}
          tabsError={tabsError}
        >
          <Tab
            label={capitalizeFirstLetter(
              intl.formatMessage({ id: 'cadastro' })
            )}
          >
            <Panel
              header={capitalizeFirstLetter(
                intl.formatMessage({ id: 'label.informacoesBasicas' })
              )}
            >
              <Row>
                <Col md={3}>
                  <Input
                    required
                    disabled={
                      isDisabled ||
                      (configuracaoPerda?.utilizarCodigoAutomatico &&
                        isNewState) ||
                      (!isNewState &&
                        !configuracaoPerda?.permitirAlterarCodigoAutomatico)
                    }
                    style={{ height: '37px' }}
                    model={{
                      label: intl.formatMessage({ id: 'idIntegracao' }),
                      value: form?.idIntegracao,
                      requestChange: (v) => handleChange('idIntegracao', v),
                      errors: errors?.idIntegracao,
                    }}
                  />
                </Col>
                <Col md={7}>
                  <Input
                    required
                    disabled={isDisabled}
                    style={{ height: '37px' }}
                    type="text"
                    model={{
                      label: intl.formatMessage({ id: 'nome' }),
                      value: form?.nome,
                      requestChange: (v) => handleChange('nome', v),
                      errors: errors?.nome,
                    }}
                  />
                </Col>
                <Col md={2}>{renderStatus(form)}</Col>
              </Row>
              <Row className="pb-3">
                <Col md={5}>
                  <OrganizacaoLookup
                    disabled={isDisabled}
                    style={{
                      height: '37px',
                      borderColor: errors?.area ? '#f05050' : '#dde6e9',
                    }}
                    required
                    model={{
                      label: capitalizeFirstLetter(resources.area),
                      value: form?.area,
                      requestChange: (v) => handleChange('area', v),
                      errors: errors?.area,
                    }}
                  />
                </Col>
                <Col md={5}>
                  <ColaboradorSimplificadoLookup
                    required
                    multi
                    style={{
                      height: '37px',
                      borderColor: errors?.responsaveis ? '#f05050' : '#dde6e9',
                    }}
                    disabled={isDisabled}
                    type="text"
                    model={{
                      label: capitalizeFirstLetter(resources.responsaveis),
                      value: form?.responsaveis,
                      requestChange: (v) => handleChange('responsaveis', v),
                      errors: errors?.responsaveis,
                    }}
                  />
                </Col>
                <Col md={2}>
                  <ArquivoUploadAnexos
                    required={false}
                    onlyOneFile={false}
                    onlyRead={isDisabled}
                    rightLabel={true}
                    classNameFormGroup="d-flex inline pt-1"
                    maxWidth={'250px'}
                    tipoAnexo={null}
                    formGroupStyle={{
                      marginBottom: 0,
                      maxWidth: '250px',
                      color: errors?.anexos && !isDisabled ? '#FF0000' : null,
                    }}
                    model={{
                      label: intl.formatMessage({ id: 'anexos' }),
                      value: form?.anexos,
                      requestChange: (v) => handleChange('anexos', v),
                      errors: errors?.anexos,
                    }}
                  />
                </Col>
              </Row>
              <Row className="pb-3">
                <Col md={12}>
                  <Input
                    disabled={isDisabled}
                    type="textarea"
                    rows={3}
                    model={{
                      label: intl.formatMessage({ id: 'descricao' }),
                      value: form?.descricao,
                      requestChange: (v) => handleChange('descricao', v),
                    }}
                  />
                </Col>
              </Row>
              <Row className="pb-3">
                <Col md={6}>
                  <InputNumber
                    required
                    precision={2}
                    style={{ height: '37px' }}
                    model={{
                      label: intl.formatMessage({ id: 'labelValorPerda' }),
                      value: form?.valorPerda,
                      requestChange: (v) => handleChange('valorPerda', v),
                      errors: errors?.valorPerda,
                    }}
                    disabled={isDisabled}
                  />
                </Col>
                <Col md={6}>
                  <TemplatePerdaOperacionalLookup
                    required
                    clearable
                    disabled={isDisabled}
                    style={{ height: '37px' }}
                    model={{
                      label: intl.formatMessage(
                        { id: 'templateAnalisePerda' },
                        { perdaoperacional: resources.perdaoperacional }
                      ),
                      value: form?.templatePerdaOperacional,
                      requestChange: (v) =>
                        handleChange('templatePerdaOperacional', v),
                      errors: errors?.templatePerdaOperacional,
                    }}
                  />
                </Col>
              </Row>
              <Row className="pb-3">
                <Col md={6}>
                  <DatePicker
                    style={{
                      height: '37px',
                      borderColor: errors?.dataRegistro ? '#f05050' : '#dde6e9',
                    }}
                    required
                    model={{
                      label: intl.formatMessage({ id: 'labelDataRegistro' }),
                      value: form.dataRegistro,
                      requestChange: (v) => handleChange('dataRegistro', v),
                      errors: errors?.dataRegistro,
                    }}
                    disabled={isDisabled}
                    timeFormat={false}
                  />
                </Col>
                <Col md={6}>
                  <DatePicker
                    style={{
                      height: '37px',
                      borderColor: errors?.dataPerda ? '#f05050' : '#dde6e9',
                    }}
                    required
                    model={{
                      label: intl.formatMessage({ id: 'labelDataOcorrencia' }),
                      value: form.dataPerda,
                      requestChange: (v) => handleChange('dataPerda', v),
                      errors: errors?.dataPerda,
                    }}
                    disabled={isDisabled}
                    timeFormat={false}
                  />
                </Col>
              </Row>
            </Panel>
            {form?.camposCustomizados &&
              form?.camposCustomizados.length > 0 && (
                <Row>
                  <Col md={12}>
                    <Panel
                      header={capitalizeFirstLetter(
                        intl.formatMessage({ id: 'informacoesComplementares' })
                      )}
                    >
                      <DesenharCampoCustomizado
                        mdFields={6}
                        mdFieldsTo={[2]}
                        heightFieldText="37px"
                        disabled={isDisabled}
                        model={{
                          label: intl.formatMessage({
                            id: 'informacoesComplementares',
                          }),
                          value: form?.camposCustomizados,
                          requestChange: (e) =>
                            handleChange('camposCustomizados', e),
                        }}
                      />
                    </Panel>
                  </Col>
                </Row>
              )}
            <Row>
              <Col md={12}>
                <Panel
                  header={capitalizeFirstLetter(
                    intl.formatMessage({ id: 'informacoesDeOrigem' })
                  )}
                >
                  <Row>
                    <Col md={6}>
                      <OrganizacaoLookup
                        disabled={isDisabled}
                        style={{
                          height: '37px',
                          borderColor: errors?.areaGeradora
                            ? '#f05050'
                            : '#dde6e9',
                        }}
                        model={{
                          label: intl.formatMessage({ id: 'areaGeradora' }),
                          value: form?.areaGeradora,
                          requestChange: (v) => handleChange('areaGeradora', v),
                          errors: errors?.areaGeradora,
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <ColaboradorSimplificadoLookup
                        disabled={isDisabled}
                        style={{
                          height: '37px',
                          borderColor: errors?.responsavelPelaAnalise
                            ? '#f05050'
                            : '#dde6e9',
                        }}
                        type="text"
                        model={{
                          label: intl.formatMessage({
                            id: 'responsavelPelaAnalise',
                          }),
                          value: form?.responsavelPelaAnalise,
                          requestChange: (v) =>
                            handleChange('responsavelPelaAnalise', v),
                          errors: errors?.responsavelPelaAnalise,
                        }}
                      />
                    </Col>
                  </Row>
                </Panel>
              </Col>
            </Row>
            {!isNew && form?.historico && (
              <Row>
                <Col md={12}>
                  <HistoricoView intl={intl} historico={form.historico} />
                </Col>
              </Row>
            )}
          </Tab>

          {(form.status?.id == PERDA_OPERACIONAL_STATUS.PENDENTE_ANALISE ||
            form.status?.id == PERDA_OPERACIONAL_STATUS.PENDENTE_REVISAO ||
            form.status?.id == PERDA_OPERACIONAL_STATUS.CONCLUIDO) && (
            <Tab
              label={intl.formatMessage({ id: 'label.tipoCombinacaoValor' })}
            >
              <PerguntasCampoCustomizado
                resources={resources}
                perdaOperacionalId={form?.id}
                utilizarPlanoAcao={form?.utilizarPlanoAcao}
                usuarioPodeModificarPlanoAcao={
                  form?.usuarioPodeModificarPlanoAcao
                }
                mdFields={12}
                rowsTextArea={5}
                model={{
                  value: form?.camposCustomizadosPerdaOperacionalAnalise,
                  requestChange: handleChangeCamposCustomizados,
                }}
                showErrors={showErrors}
              />
            </Tab>
          )}

          {(form.status?.id == PERDA_OPERACIONAL_STATUS.PENDENTE_ANALISE ||
            form.status?.id == PERDA_OPERACIONAL_STATUS.PENDENTE_REVISAO ||
            form.status?.id == PERDA_OPERACIONAL_STATUS.CONCLUIDO) &&
            !isNew && (
              <Tab label={capitalizeFirstLetter(resources.planodeacao)}>
                <Acao
                  parameters={{
                    perdaOperacionalId: form?.id,
                    exibirLookups: false,
                    itemNaoObrigatorio: true,
                  }}
                  exibirLookups={false}
                  handleCloseModal={handleClose}
                  showMenuToolBar={false}
                  forceRefreshAfterChange={true}
                  ignorarFiltroSalvo={true}
                  desabilitarSincronizacao={true}
                />
              </Tab>
            )}
        </Tabs>
      </LoadingContainer>
    </Dialog>
  );
};

export default PerdaOperacionalEdit;
